import * as React from 'react';
import { css } from '@emotion/react';
import { endDirection, FixMeLater, startDirection } from '../../utils';
import { ButtonBase } from '../ButtonBase/ButtonBase';
import { useHoneycombTheme } from '../../theming';
import { SvgIconProps } from '../SvgIcon';

export type MenuLinkTheme = {
    paddingX: number;
    paddingY: number;
};

export const menuLinkTheme = {
    paddingX: 12,
    paddingY: 12,
};

export type MenuLinkProps = {
    /**
     * If `true`, the menu link will be a button (using `ButtonBase`).
     * Use this if you want to apply an `onClick` handler.
     * @default false
     */
    button?: boolean;

    /**
     * The label which will be displayed on the menu link.
     */
    children: React.ReactNode;

    /**
     * An svg icon component.
     */
    icon?: React.ReactElement<SvgIconProps>;

    /**
     * If `true`, the option will be in the selected state.
     * @default false
     */
    selected?: boolean;

    /**
     * If `true`, the option will have additional start padding.
     * @default false
     */
    stepIn?: boolean;

    /**
     * Permit additional properties which will be passed to the root node.
     */
    [x: string]: unknown;

    /**
     * A reference passed to the root element.
     */
    ref?: React.Ref<FixMeLater>;
};

export function MenuLink(props: Readonly<MenuLinkProps>): React.JSX.Element {
    const { button, children, selected, icon, stepIn = false, ref, ...other } = props;

    const { theme } = useHoneycombTheme();
    const { palette, typography } = theme;

    const selectedColor = palette.system.info;

    const rootStyles = css`
        display: flex;
        align-items: center;
        padding: ${menuLinkTheme.paddingY}px ${menuLinkTheme.paddingX}px;
        ${stepIn &&
        `
                padding-${startDirection()}: 24px;
            `}
        &:focus-visible {
            box-shadow: 0 0 0 2px ${palette.action.focus};
        }
        ${selected &&
        `
                color: ${selectedColor.base};
                background-color: ${selectedColor.lightest};
                font-weight: ${typography.family.body.fontWeight.bold};
            `}
        ${!selected &&
        `
                &:hover {
                    background-color: ${palette.background.offset};
                }
            `}
    `;

    const iconStyles = css`
            flex-shrink: 0;
            width: 24px;
            height: 24px;
            margin-${endDirection()}: ${8}px;
        `;

    return (
        <ButtonBase ref={ref} css={rootStyles} role="menuitem" component="div" {...other}>
            {icon &&
                React.cloneElement(icon, {
                    css: iconStyles,
                    size: 's',
                    ...icon.props,
                })}
            <span>{children}</span>
        </ButtonBase>
    );
}
